import {
  getNoteAtPos,
  NoteName,
  noteToSemitone,
  semitoneToNote,
  TUNING_PRESETS,
} from './musicTheory';
import { StageHighlight } from '../features/jamcam-stage/types';

export type PentatonicFlavor = 'major' | 'minor';
export type ChordQuality = 'major' | 'minor' | 'dominant';

export interface ProgressionDegree {
  numeral: string;
  interval: number;
  quality: ChordQuality;
}

export type Tonality = 'major' | 'minor';

export interface ProgressionOption {
  id: string;
  label: string;
  tonality: Tonality;
  degrees: ProgressionDegree[];
}

export interface ResolvedChord {
  numeral: string;
  root: NoteName;
  quality: ChordQuality;
  label: string;
  notes: NoteName[];
}

export interface ChordVoicingOption {
  id: string;
  label: string;
  highlights: StageHighlight[];
  fretRange: [number, number];
}

export const STANDARD_TUNING = TUNING_PRESETS[0].notes;

export const PRACTICE_KEYS: NoteName[] = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
];

export function isPracticeKey(value: unknown): value is NoteName {
  return typeof value === 'string' && PRACTICE_KEYS.includes(value as NoteName);
}

export const TRIAD_GROUPS = [
  { label: 'E-A-D', strings: [0, 1, 2] },
  { label: 'A-D-G', strings: [1, 2, 3] },
  { label: 'D-G-B', strings: [2, 3, 4] },
  { label: 'G-B-e', strings: [3, 4, 5] },
] as const;

const PENTATONIC_BOXES = [
  [
    [0, 5],
    [0, 8],
    [1, 5],
    [1, 7],
    [2, 5],
    [2, 7],
    [3, 5],
    [3, 7],
    [4, 5],
    [4, 8],
    [5, 5],
    [5, 8],
  ],
  [
    [0, 8],
    [0, 10],
    [1, 7],
    [1, 10],
    [2, 7],
    [2, 10],
    [3, 7],
    [3, 9],
    [4, 8],
    [4, 10],
    [5, 8],
    [5, 10],
  ],
  [
    [0, 10],
    [0, 12],
    [1, 10],
    [1, 12],
    [2, 10],
    [2, 12],
    [3, 9],
    [3, 12],
    [4, 10],
    [4, 13],
    [5, 10],
    [5, 12],
  ],
  [
    [0, 12],
    [0, 15],
    [1, 12],
    [1, 15],
    [2, 12],
    [2, 14],
    [3, 12],
    [3, 14],
    [4, 13],
    [4, 15],
    [5, 12],
    [5, 15],
  ],
  [
    [0, 3],
    [0, 5],
    [1, 3],
    [1, 5],
    [2, 2],
    [2, 5],
    [3, 2],
    [3, 5],
    [4, 3],
    [4, 5],
    [5, 3],
    [5, 5],
  ],
] as const;

const OPEN_STRING_SEMITONES = STANDARD_TUNING.map((note) =>
  noteToSemitone(note)
);
const LOW_E_SEMITONE = OPEN_STRING_SEMITONES[0];
interface BarreSpec {
  fret: number;
  startString: number;
  endString: number;
  anchorString?: number;
}

interface VoicingTemplate {
  id: string;
  label: string;
  frets: Array<number | null>;
  barres?: BarreSpec[];
}

const OPEN_MAJOR_VOICINGS: Partial<Record<NoteName, VoicingTemplate>> = {
  C: { id: 'open-c', label: 'Open C', frets: [null, 3, 2, 0, 1, 0] },
  A: { id: 'open-a', label: 'Open A', frets: [null, 0, 2, 2, 2, 0] },
  G: { id: 'open-g', label: 'Open G', frets: [3, 2, 0, 0, 0, 3] },
  E: { id: 'open-e', label: 'Open E', frets: [0, 2, 2, 1, 0, 0] },
  D: { id: 'open-d', label: 'Open D', frets: [null, null, 0, 2, 3, 2] },
};

const OPEN_MINOR_VOICINGS: Partial<Record<NoteName, VoicingTemplate>> = {
  A: { id: 'open-am', label: 'Open Am', frets: [null, 0, 2, 2, 1, 0] },
  E: { id: 'open-em', label: 'Open Em', frets: [0, 2, 2, 0, 0, 0] },
  D: { id: 'open-dm', label: 'Open Dm', frets: [null, null, 0, 2, 3, 1] },
};

const OPEN_DOMINANT_VOICINGS: Partial<Record<NoteName, VoicingTemplate>> = {
  C: { id: 'open-c7', label: 'Open C7', frets: [null, 3, 2, 3, 1, 0] },
  A: { id: 'open-a7', label: 'Open A7', frets: [null, 0, 2, 0, 2, 0] },
  G: { id: 'open-g7', label: 'Open G7', frets: [3, 2, 0, 0, 0, 1] },
  E: { id: 'open-e7', label: 'Open E7', frets: [0, 2, 0, 1, 0, 0] },
  D: { id: 'open-d7', label: 'Open D7', frets: [null, null, 0, 2, 1, 2] },
};

/**
 * Movable CAGED-system shape. `offsets` are fret distances from the
 * root fret on `anchorString`; `null` means the string is muted.
 * `minRootFret` is required when any offset is negative (i.e. some
 * notes sit below the anchor's root fret), so the shape is only
 * generated when it fits the neck.
 */
interface MovableShape {
  idPrefix: string;
  labelPrefix: string;
  anchorString: number;
  offsets: Array<number | null>;
  barres?: Array<{
    startString: number;
    endString: number;
    fretOffset: number;
    anchorString?: number;
  }>;
  minRootFret?: number;
}

// Major CAGED shapes (E/A/D/C/G), positioned with the chord root on
// the anchor string. Numbers are derived from the corresponding open
// chord, expressed as offsets from the anchor root fret.
const MAJOR_MOVABLE_SHAPES: MovableShape[] = [
  {
    // Open E: [0,2,2,1,0,0]; root on low E (anchor fret R).
    idPrefix: 'e-major',
    labelPrefix: 'E-Shape Barre',
    anchorString: 0,
    offsets: [0, 2, 2, 1, 0, 0],
    barres: [{ startString: 0, endString: 5, fretOffset: 0, anchorString: 0 }],
  },
  {
    // Open A: [null,0,2,2,2,0]; root on A string (anchor fret R).
    idPrefix: 'a-major',
    labelPrefix: 'A-Shape Barre',
    anchorString: 1,
    offsets: [null, 0, 2, 2, 2, 0],
    barres: [{ startString: 1, endString: 5, fretOffset: 0, anchorString: 1 }],
  },
  {
    // Open D: [null,null,0,2,3,2]; root on D string (anchor fret R).
    idPrefix: 'd-major',
    labelPrefix: 'D-Shape',
    anchorString: 2,
    offsets: [null, null, 0, 2, 3, 2],
  },
  {
    // Open C: [null,3,2,0,1,0] → offsets from rootFret=3 on A string.
    idPrefix: 'c-major',
    labelPrefix: 'C-Shape',
    anchorString: 1,
    offsets: [null, 0, -1, -3, -2, -3],
    minRootFret: 3,
  },
  {
    // Open G: [3,2,0,0,0,3] → offsets from rootFret=3 on low E string.
    idPrefix: 'g-major',
    labelPrefix: 'G-Shape',
    anchorString: 0,
    offsets: [0, -1, -3, -3, -3, 0],
    minRootFret: 3,
  },
];

const MINOR_MOVABLE_SHAPES: MovableShape[] = [
  {
    // Open Em: [0,2,2,0,0,0]; root on low E.
    idPrefix: 'e-minor',
    labelPrefix: 'Em-Shape Barre',
    anchorString: 0,
    offsets: [0, 2, 2, 0, 0, 0],
    barres: [{ startString: 0, endString: 5, fretOffset: 0, anchorString: 0 }],
  },
  {
    // Open Am: [null,0,2,2,1,0]; root on A.
    idPrefix: 'a-minor',
    labelPrefix: 'Am-Shape Barre',
    anchorString: 1,
    offsets: [null, 0, 2, 2, 1, 0],
    barres: [{ startString: 1, endString: 5, fretOffset: 0, anchorString: 1 }],
  },
  {
    // Open Dm: [null,null,0,2,3,1]; root on D.
    idPrefix: 'd-minor',
    labelPrefix: 'Dm-Shape',
    anchorString: 2,
    offsets: [null, null, 0, 2, 3, 1],
  },
];

const DOMINANT_MOVABLE_SHAPES: MovableShape[] = [
  {
    // Open E7: [0,2,0,1,0,0]; root on low E.
    idPrefix: 'e7',
    labelPrefix: 'E7-Shape Barre',
    anchorString: 0,
    offsets: [0, 2, 0, 1, 0, 0],
    barres: [{ startString: 0, endString: 5, fretOffset: 0, anchorString: 0 }],
  },
  {
    // Open A7: [null,0,2,0,2,0]; root on A.
    idPrefix: 'a7',
    labelPrefix: 'A7-Shape Barre',
    anchorString: 1,
    offsets: [null, 0, 2, 0, 2, 0],
    barres: [{ startString: 1, endString: 5, fretOffset: 0, anchorString: 1 }],
  },
  {
    // Open D7: [null,null,0,2,1,2]; root on D.
    idPrefix: 'd7',
    labelPrefix: 'D7-Shape',
    anchorString: 2,
    offsets: [null, null, 0, 2, 1, 2],
  },
  {
    // Open C7: [null,3,2,3,1,0] → offsets from rootFret=3 on A.
    idPrefix: 'c7',
    labelPrefix: 'C7-Shape',
    anchorString: 1,
    offsets: [null, 0, -1, 0, -2, -3],
    minRootFret: 3,
  },
  {
    // Open G7: [3,2,0,0,0,1] → offsets from rootFret=3 on low E.
    idPrefix: 'g7',
    labelPrefix: 'G7-Shape',
    anchorString: 0,
    offsets: [0, -1, -3, -3, -3, -2],
    minRootFret: 3,
  },
];

const MOVABLE_SHAPES_BY_QUALITY: Record<ChordQuality, MovableShape[]> = {
  major: MAJOR_MOVABLE_SHAPES,
  minor: MINOR_MOVABLE_SHAPES,
  dominant: DOMINANT_MOVABLE_SHAPES,
};

export const PROGRESSION_OPTIONS: ProgressionOption[] = [
  {
    id: 'i-vi-iv-v',
    label: 'I - vi - IV - V',
    tonality: 'major',
    degrees: [
      { numeral: 'I', interval: 0, quality: 'major' },
      { numeral: 'vi', interval: 9, quality: 'minor' },
      { numeral: 'IV', interval: 5, quality: 'major' },
      { numeral: 'V', interval: 7, quality: 'major' },
    ],
  },
  {
    id: 'i-iv-v-i',
    label: 'I - IV - V - I',
    tonality: 'major',
    degrees: [
      { numeral: 'I', interval: 0, quality: 'major' },
      { numeral: 'IV', interval: 5, quality: 'major' },
      { numeral: 'V', interval: 7, quality: 'major' },
      { numeral: 'I', interval: 0, quality: 'major' },
    ],
  },
  {
    id: 'ii-v-i',
    label: 'ii - V - I',
    tonality: 'major',
    degrees: [
      { numeral: 'ii', interval: 2, quality: 'minor' },
      { numeral: 'V', interval: 7, quality: 'dominant' },
      { numeral: 'I', interval: 0, quality: 'major' },
    ],
  },
  {
    id: 'blues-turnaround',
    label: 'I7 - IV7 - I7 - V7',
    tonality: 'major',
    degrees: [
      { numeral: 'I', interval: 0, quality: 'dominant' },
      { numeral: 'IV', interval: 5, quality: 'dominant' },
      { numeral: 'I', interval: 0, quality: 'dominant' },
      { numeral: 'V', interval: 7, quality: 'dominant' },
    ],
  },
  {
    id: 'i-iv-v-minor',
    label: 'i - iv - V',
    tonality: 'minor',
    degrees: [
      { numeral: 'i', interval: 0, quality: 'minor' },
      { numeral: 'iv', interval: 5, quality: 'minor' },
      { numeral: 'V', interval: 7, quality: 'dominant' },
    ],
  },
  {
    id: 'i-vi-iii-vii-minor',
    label: 'i - VI - III - VII',
    tonality: 'minor',
    degrees: [
      { numeral: 'i', interval: 0, quality: 'minor' },
      { numeral: 'VI', interval: 8, quality: 'major' },
      { numeral: 'III', interval: 3, quality: 'major' },
      { numeral: 'VII', interval: 10, quality: 'major' },
    ],
  },
];

function transposeNote(root: NoteName, interval: number): NoteName {
  return semitoneToNote(noteToSemitone(root) + interval);
}

function wrapFret(fret: number): number {
  let nextFret = fret;
  while (nextFret < 0) nextFret += 12;
  while (nextFret > 20) nextFret -= 12;
  return nextFret;
}

export function getDisplayScaleName(flavor: PentatonicFlavor): string {
  return flavor === 'major' ? 'Major Pentatonic' : 'Minor Pentatonic';
}

// User-position (0-indexed 0..4) → box index, for major pentatonic.
// Major Pos 1 = the "root on low-E under the index finger" shape,
// which is the minor pent box[1] one octave below. Pos 5 wraps to
// box[0] but an octave up (so it sits ABOVE pos 4, not below pos 1).
const MAJOR_BOX_ORDER = [1, 2, 3, 4, 0] as const;

/**
 * Compute the octave adjustment (multiple of 12) that should be
 * added to each major-pentatonic position so the boxes ascend
 * monotonically up the neck for a given root shift.
 */
function computeMajorOctaveAdjustments(shift: number): number[] {
  const adjustments = new Array<number>(5).fill(0);
  let prevMin = -Infinity;
  for (let i = 0; i < 5; i++) {
    const box = PENTATONIC_BOXES[MAJOR_BOX_ORDER[i]];
    let min = Math.min(...box.map(([, f]) => f + shift));
    while (min <= prevMin) {
      adjustments[i] += 12;
      min += 12;
    }
    prevMin = min;
  }
  return adjustments;
}

export function getPentatonicBoxHighlights(
  root: NoteName,
  flavor: PentatonicFlavor,
  position: number
): StageHighlight[] {
  const effectiveRoot = flavor === 'major' ? transposeNote(root, -3) : root;
  const rootSemitone = noteToSemitone(effectiveRoot);
  const targetRootFret = (((rootSemitone - LOW_E_SEMITONE) % 12) + 12) % 12;
  const shift = targetRootFret - 5;

  const positionIdx = ((position % 5) + 5) % 5;
  let boxIndex: number;
  let octaveAdjust = 0;
  if (flavor === 'major') {
    boxIndex = MAJOR_BOX_ORDER[positionIdx];
    octaveAdjust = computeMajorOctaveAdjustments(shift)[positionIdx];
  } else {
    boxIndex = positionIdx;
  }
  const box = PENTATONIC_BOXES[boxIndex] ?? PENTATONIC_BOXES[0];

  return box.map(([stringIndex, fret]) => {
    const nextFret = wrapFret(fret + shift + octaveAdjust);
    // Root highlighting is now opt-in via HighlightSettings; the base
    // highlight just uses the standard scale-note blue.
    return {
      string: stringIndex,
      fret: nextFret,
      fillColor: 'rgba(86, 189, 255, 0.68)',
    };
  });
}

export function getPentatonicVisibleRange(
  highlights: StageHighlight[]
): [number, number] {
  const frets = highlights.map((highlight) => highlight.fret);
  return [
    Math.max(0, Math.min(...frets) - 1),
    Math.min(20, Math.max(...frets) + 1),
  ];
}

/* ------------------------------------------------------------------ */
/*  Blues scale positions (5 boxes)                                    */
/* ------------------------------------------------------------------ */

// The "blue note" added to each pentatonic box. Minor blues adds the
// b5 (interval 6) to the minor pentatonic; major blues adds the b3
// (interval 3) to the major pentatonic.
const MINOR_BLUE_NOTE_INTERVAL = 6;
const MAJOR_BLUE_NOTE_INTERVAL = 3;

/** Display name for a blues-scale flavor. */
export function getBluesDisplayName(flavor: PentatonicFlavor): string {
  return flavor === 'major' ? 'Major Blues' : 'Minor Blues';
}

/** Display name for a diatonic-scale flavor. */
export function getDiatonicDisplayName(flavor: PentatonicFlavor): string {
  return flavor === 'major' ? 'Major Scale' : 'Minor Scale';
}

const SCALE_FILL = 'rgba(86, 189, 255, 0.68)';

/**
 * Highlights for a single blues-scale box position (0-indexed).
 *
 * Uses the exact same box shapes as the pentatonic positions, then
 * adds the single "blue note" (minor: b5, major: b3) only where it fills
 * the gap between two adjacent box notes on the same string. This keeps
 * the blues boxes identical to the pentatonic boxes plus the idiomatic
 * passing tone, without tacking the blue note onto the edges of the box.
 */
export function getBluesBoxHighlights(
  root: NoteName,
  position: number,
  flavor: PentatonicFlavor = 'minor'
): StageHighlight[] {
  // Base shape: the exact pentatonic box for this position/flavor.
  const highlights = getPentatonicBoxHighlights(root, flavor, position);
  if (highlights.length === 0) return highlights;

  // The blue note semitone (b5 for minor blues, b3 for major blues).
  const rootSemi = noteToSemitone(root);
  const blueInterval =
    flavor === 'major' ? MAJOR_BLUE_NOTE_INTERVAL : MINOR_BLUE_NOTE_INTERVAL;
  const blueSemi = (((rootSemi + blueInterval) % 12) + 12) % 12;

  // Add the blue note only where it sits between two existing box notes
  // on the same string (i.e. it fills a whole-step gap). This avoids
  // adding it at the edges of the box where it isn't part of the shape.
  const occupied = new Set(highlights.map((h) => `${h.string},${h.fret}`));
  for (let stringIndex = 0; stringIndex < 6; stringIndex++) {
    const openSemi = OPEN_STRING_SEMITONES[stringIndex];
    for (let fret = 0; fret <= 22; fret++) {
      if ((((openSemi + fret) % 12) + 12) % 12 !== blueSemi) continue;
      const key = `${stringIndex},${fret}`;
      if (occupied.has(key)) continue;
      // Only fill when both neighbouring frets on this string are box notes.
      if (
        !occupied.has(`${stringIndex},${fret - 1}`) ||
        !occupied.has(`${stringIndex},${fret + 1}`)
      ) {
        continue;
      }
      occupied.add(key);
      highlights.push({
        string: stringIndex,
        fret,
        fillColor: SCALE_FILL,
      });
    }
  }
  return highlights;
}

/* ------------------------------------------------------------------ */
/*  Diatonic (major/minor scale) positions (7 boxes, 3-notes-per-string) */
/* ------------------------------------------------------------------ */

// Major scale intervals: R, 2, 3, 4, 5, 6, 7
const MAJOR_DIATONIC_INTERVALS = [0, 2, 4, 5, 7, 9, 11] as const;
// Natural minor intervals: R, 2, b3, 4, 5, b6, b7
const MINOR_DIATONIC_INTERVALS = [0, 2, 3, 5, 7, 8, 10] as const;

/**
 * Highlights for a single 3-notes-per-string diatonic position
 * (1-indexed 1..7), following the fretscience.com "grand unification"
 * 3-NPS system. Each pattern is named by the scale degree of its first
 * note on the low E string: pattern 1 starts on the root (Ionian shape),
 * pattern 2 on the 2nd degree (Dorian), … pattern 7 on the 7th (Locrian).
 * Patterns ascend the neck, but if any note climbs above fret 17 the
 * whole shape loops back down an octave (e.g. G-major pattern 7 starts
 * on fret 2 under the index finger instead of fret 14). The `flavor`
 * argument selects the major scale (Ionian) or natural minor (Aeolian).
 */
export function getDiatonicBoxHighlights(
  root: NoteName,
  position: number,
  flavor: PentatonicFlavor = 'major'
): StageHighlight[] {
  const rootSemi = noteToSemitone(root);
  const positionIdx = (((position - 1) % 7) + 7) % 7;
  const intervals =
    flavor === 'major' ? MAJOR_DIATONIC_INTERVALS : MINOR_DIATONIC_INTERVALS;

  // Pattern N starts on scale degree N on the low E string. Anchor fret
  // = that starting degree's fret, walked up the neck from the root so
  // successive patterns ascend.
  let lowEAnchor = (((rootSemi - LOW_E_SEMITONE) % 12) + 12) % 12;
  for (let i = 0; i < positionIdx; i++) {
    lowEAnchor += (intervals[i + 1] - intervals[i] + 12) % 12;
  }

  const highlights: StageHighlight[] = [];
  let degreeIdx = positionIdx;
  for (let stringIndex = 0; stringIndex < 6; stringIndex++) {
    const openSemi = OPEN_STRING_SEMITONES[stringIndex];
    let prevFret = -1;
    for (let n = 0; n < 3; n++) {
      const dIdx = (degreeIdx + n) % 7;
      const pc = (((rootSemi + intervals[dIdx]) % 12) + 12) % 12;
      let fret = (((pc - openSemi) % 12) + 12) % 12;
      // Pull the first note on this string into the position's window
      // (lowEAnchor − 2 .. lowEAnchor + 6 spans the natural 3-NPS box).
      if (n === 0) {
        while (fret < lowEAnchor - 2) fret += 12;
        while (fret > lowEAnchor + 6) fret -= 12;
      } else {
        // Subsequent notes ascend monotonically on the string.
        while (fret <= prevFret) fret += 12;
      }
      // Root highlighting is now opt-in via HighlightSettings — all
      // base scale dots use the standard scale-note blue.
      highlights.push({
        string: stringIndex,
        fret,
        fillColor: SCALE_FILL,
      });
      prevFret = fret;
    }
    degreeIdx = (degreeIdx + 3) % 7;
  }

  // Keep the whole shape on the neck. If any note climbs above fret 17,
  // loop the entire pattern down an octave (G-major pattern 7 lands on
  // fret 2 instead of 14). Lift it back up if it drops below the nut.
  let maxFret = Math.max(...highlights.map((h) => h.fret));
  let minFret = Math.min(...highlights.map((h) => h.fret));
  while (maxFret > 17) {
    for (const h of highlights) h.fret -= 12;
    maxFret -= 12;
    minFret -= 12;
  }
  while (minFret < 0) {
    for (const h of highlights) h.fret += 12;
    minFret += 12;
  }

  return highlights;
}

export function getChordNotes(
  root: NoteName,
  quality: ChordQuality
): NoteName[] {
  const intervals =
    quality === 'minor'
      ? [0, 3, 7]
      : quality === 'dominant'
      ? [0, 4, 7, 10]
      : [0, 4, 7];

  return intervals.map((interval) => transposeNote(root, interval));
}

function getOpenVoicingTemplate(
  root: NoteName,
  quality: ChordQuality
): VoicingTemplate | null {
  if (quality === 'minor') return OPEN_MINOR_VOICINGS[root] ?? null;
  if (quality === 'dominant') return OPEN_DOMINANT_VOICINGS[root] ?? null;
  return OPEN_MAJOR_VOICINGS[root] ?? null;
}

function getHighlightFretRange(highlights: StageHighlight[]): [number, number] {
  const frets = highlights.map((highlight) => highlight.fret);
  return [
    Math.max(0, Math.min(...frets) - 1),
    Math.min(20, Math.max(...frets) + 1),
  ];
}

function buildVoicingHighlights(
  _root: NoteName,
  frets: Array<number | null>,
  barres: BarreSpec[] = [],
  tuningNotes: NoteName[] = STANDARD_TUNING
): StageHighlight[] {
  const skippedBarrePositions = new Set<string>();
  const barreMap = new Map<string, BarreSpec>();

  for (const barre of barres) {
    const anchorString = barre.anchorString ?? barre.startString;
    barreMap.set(`${barre.fret}:${anchorString}`, {
      ...barre,
      anchorString,
    });

    for (let string = barre.startString; string <= barre.endString; string++) {
      if (string === anchorString) continue;
      skippedBarrePositions.add(`${barre.fret}:${string}`);
    }
  }

  return frets.flatMap((fret, string) => {
    if (fret === null || skippedBarrePositions.has(`${fret}:${string}`)) {
      return [];
    }

    const barre = barreMap.get(`${fret}:${string}`);

    return [
      {
        fret,
        string,
        useScaleNoteStyle: true,
        ...(barre
          ? {
              barreFromString: barre.startString,
              barreToString: barre.endString,
            }
          : {}),
      },
    ];
  });
}

export function getChordVoicingOptions(
  root: NoteName,
  quality: ChordQuality,
  tuningNotes: NoteName[] = STANDARD_TUNING
): ChordVoicingOption[] {
  const options: ChordVoicingOption[] = [];
  const seen = new Set<string>();
  const MAX_FRET = 15;

  const addVoicing = (template: VoicingTemplate) => {
    const highlights = buildVoicingHighlights(
      root,
      template.frets,
      template.barres,
      tuningNotes
    );
    if (highlights.length === 0) return;
    // Dedupe identical fingerings (e.g. C-shape at rootFret 3 == Open C).
    const key = highlights
      .map((h) => `${h.string}:${h.fret}`)
      .sort()
      .join('|');
    if (seen.has(key)) return;
    seen.add(key);
    options.push({
      id: template.id,
      label: template.label,
      highlights,
      fretRange: getHighlightFretRange(highlights),
    });
  };

  const openVoicing = getOpenVoicingTemplate(root, quality);
  if (openVoicing) addVoicing(openVoicing);

  const shapes = MOVABLE_SHAPES_BY_QUALITY[quality] ?? [];
  for (const shape of shapes) {
    const anchorOpenSemi = OPEN_STRING_SEMITONES[shape.anchorString];
    const baseRootFret =
      (((noteToSemitone(root) - anchorOpenSemi) % 12) + 12) % 12;

    // Try the shape at its base fret and one octave up so players see
    // both lower- and higher-neck variations when they fit.
    for (const octaveShift of [0, 12]) {
      const rootFret = baseRootFret + octaveShift;
      // rootFret === 0 would re-create the open voicing for this root.
      if (rootFret === 0) continue;
      if (shape.minRootFret !== undefined && rootFret < shape.minRootFret) {
        continue;
      }

      const frets = shape.offsets.map((o) =>
        o === null ? null : rootFret + o
      );
      if (frets.some((f) => f !== null && (f < 0 || f > MAX_FRET))) {
        continue;
      }

      const barres = (shape.barres ?? []).map((b) => ({
        fret: rootFret + b.fretOffset,
        startString: b.startString,
        endString: b.endString,
        anchorString: b.anchorString,
      }));

      addVoicing({
        id: `${shape.idPrefix}-${rootFret}`,
        label: `${shape.labelPrefix} ${rootFret}`,
        frets,
        barres,
      });
    }
  }

  // Sort by the lowest fret in the voicing so open / low-position
  // shapes always appear first.
  const minFretFor = (option: ChordVoicingOption): number => {
    const frets = option.highlights.map((h) => h.fret);
    return frets.length === 0 ? 0 : Math.min(...frets);
  };
  options.sort((a, b) => minFretFor(a) - minFretFor(b));

  return options;
}

export function resolveProgression(
  keyRoot: NoteName,
  progressionId: string
): ResolvedChord[] {
  const progression =
    PROGRESSION_OPTIONS.find((option) => option.id === progressionId) ??
    PROGRESSION_OPTIONS[0];

  return progression.degrees.map((degree) => {
    const root = transposeNote(keyRoot, degree.interval);
    const label = `${degree.numeral} · ${root}${
      degree.quality === 'minor'
        ? 'm'
        : degree.quality === 'dominant'
        ? '7'
        : ''
    }`;
    return {
      numeral: degree.numeral,
      root,
      quality: degree.quality,
      label,
      notes: getChordNotes(root, degree.quality),
    };
  });
}

export function getChordToneHighlights(
  chord: ResolvedChord,
  tuningNotes: NoteName[] = STANDARD_TUNING,
  fretCount = 20
): StageHighlight[] {
  const chordNotes = new Set(chord.notes);
  const tuningSemitones = tuningNotes.map((note) => noteToSemitone(note));
  const highlights: StageHighlight[] = [];

  for (
    let stringIndex = 0;
    stringIndex < tuningSemitones.length;
    stringIndex++
  ) {
    for (let fret = 0; fret <= fretCount; fret++) {
      const noteName = semitoneToNote(tuningSemitones[stringIndex] + fret);
      if (!chordNotes.has(noteName)) continue;
      highlights.push({
        fret,
        string: stringIndex,
        useScaleNoteStyle: true,
      });
    }
  }

  return highlights;
}

export function getTriadVoicingHighlights(
  root: NoteName,
  quality: 'major' | 'minor' | 'augmented' | 'diminished',
  groupIndex: number,
  inversion: number
): StageHighlight[] {
  const group = TRIAD_GROUPS[groupIndex] ?? TRIAD_GROUPS[0];
  const rootSemitone = noteToSemitone(root);
  const qualityIntervals =
    quality === 'diminished'
      ? [0, 3, 6]
      : quality === 'augmented'
      ? [0, 4, 8]
      : quality === 'minor'
      ? [0, 3, 7]
      : [0, 4, 7];
  const rotated = Array.from({ length: 3 }, (_, index) => {
    return qualityIntervals[(index + inversion) % qualityIntervals.length];
  });

  const notes = group.strings.map((stringIndex, index) => {
    const targetSemitone = (rootSemitone + rotated[index]) % 12;
    const noteName = semitoneToNote(targetSemitone);
    return {
      string: stringIndex,
      fret: (targetSemitone - OPEN_STRING_SEMITONES[stringIndex] + 12) % 12,
      noteName,
      isRoot: rotated[index] === 0,
    };
  });

  const bassFret = notes[0]?.fret ?? 0;
  for (let index = 1; index < notes.length; index++) {
    while (notes[index].fret < bassFret - 2) notes[index].fret += 12;
    while (notes[index].fret > bassFret + 5) notes[index].fret -= 12;
  }

  if (notes.some((note) => note.fret < 0)) {
    for (const note of notes) note.fret += 12;
  }

  return notes.map((note) => ({
    fret: note.fret,
    string: note.string,
    // Root highlighting is now opt-in via HighlightSettings — all
    // base scale dots use the standard scale-note blue.
    fillColor: 'rgba(110, 188, 255, 0.7)',
  }));
}

export function getHighlightTargetNotes(
  highlights: StageHighlight[],
  tuningNotes: NoteName[] = STANDARD_TUNING
): NoteName[] {
  return Array.from(
    new Set(
      highlights.map((highlight) =>
        getNoteAtPos(highlight.fret, highlight.string, tuningNotes)
      )
    )
  ) as NoteName[];
}

export function withTrackedHighlightState(
  highlights: StageHighlight[],
  matchedNotes: NoteName[],
  activeNote: NoteName | null,
  tuningNotes: NoteName[] = STANDARD_TUNING
): StageHighlight[] {
  const matchedSet = new Set(matchedNotes);

  return highlights.map((highlight) => {
    const noteName = getNoteAtPos(
      highlight.fret,
      highlight.string,
      tuningNotes
    );
    const isMatched = matchedSet.has(noteName);

    return {
      ...highlight,
      fillColor: isMatched ? 'rgba(94, 230, 149, 0.82)' : highlight.fillColor,
    };
  });
}

/**
 * Position-based highlight state — marks specific fret+string positions as
 * matched (green) or actively playing (bright white border).  Unlike
 * `withTrackedHighlightState` which matches by note NAME, this only colours
 * the exact positions supplied by the audio+vision fusion system.
 */
export function withPositionTrackedHighlightState(
  highlights: StageHighlight[],
  matchedPositions: Set<string>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _activePositions: Set<string>
): StageHighlight[] {
  // Matched dots lock into a stable green fill + dark-green border for
  // the remainder of the shape. We intentionally do NOT light up an
  // "active" (currently-being-played-but-not-yet-matched) state here:
  // doing so caused matched dots to visually flicker every time the
  // player struck a different note, which read as the green
  // "reverting". Dots are now exactly one of two visual states:
  // default (whatever the module set) or matched-green.
  return highlights.map((highlight) => {
    const key = `${highlight.fret}:${highlight.string}`;
    if (matchedPositions.has(key)) {
      return {
        ...highlight,
        fillColor: 'rgba(94, 230, 149, 0.95)',
        borderColor: 'rgba(40, 170, 90, 1)',
      };
    }
    return highlight;
  });
}

/* ------------------------------------------------------------------ */
/*  Chord-tone tier classification (4-tier advice system)              */
/* ------------------------------------------------------------------ */

export type NoteTier = 'chord-tone' | 'strong-scale' | 'passing' | 'avoid';

export const TIER_COLORS: Record<NoteTier, string> = {
  'chord-tone': 'rgba(79, 195, 247, 0.82)', // #4fc3f7 blue
  'strong-scale': 'rgba(76, 175, 80, 0.8)', // darker green
  passing: 'rgba(255, 214, 79, 0.82)', // clearer yellow
  avoid: 'rgba(229, 115, 115, 0.68)', // #e57373 red
};

const TIER_BORDER_COLORS: Record<NoteTier, string> = {
  'chord-tone': 'rgba(18, 125, 187, 0.96)',
  'strong-scale': 'rgba(33, 117, 36, 0.96)',
  passing: 'rgba(194, 145, 24, 0.96)',
  avoid: 'rgba(165, 66, 66, 0.92)',
};

/** Build a tier-color map from guided-note config (falls back to defaults) */
export function buildTierColors(guidedNotes?: {
  chordTone?: string;
  strongScale?: string;
  passing?: string;
  avoid?: string;
}): Record<NoteTier, string> {
  return {
    'chord-tone': guidedNotes?.chordTone ?? TIER_COLORS['chord-tone'],
    'strong-scale': guidedNotes?.strongScale ?? TIER_COLORS['strong-scale'],
    passing: guidedNotes?.passing ?? TIER_COLORS.passing,
    avoid: guidedNotes?.avoid ?? TIER_COLORS.avoid,
  };
}

const CHORD_TONE_INTERVALS: Record<string, number[]> = {
  major: [0, 4, 7],
  minor: [0, 3, 7],
  dominant: [0, 4, 7, 10],
};

const MAJOR_SCALE = [0, 2, 4, 5, 7, 9, 11];

export function classifyNote(
  semitone: number,
  keyRootSemitone: number,
  chordRootSemitone: number,
  chordQuality: ChordQuality
): NoteTier {
  const ivlFromChord = (((semitone - chordRootSemitone) % 12) + 12) % 12;
  const ivlFromKey = (((semitone - keyRootSemitone) % 12) + 12) % 12;

  const chordTones = CHORD_TONE_INTERVALS[chordQuality] ?? [0, 4, 7];
  if (chordTones.includes(ivlFromChord)) return 'chord-tone';

  const isInKey = MAJOR_SCALE.includes(ivlFromKey);
  const isMinor = chordQuality === 'minor';
  const pentIntervals = isMinor ? [0, 3, 5, 7, 10] : [0, 2, 4, 7, 9];
  const isPent = pentIntervals.includes(ivlFromChord);

  if (isPent && isInKey) return 'strong-scale';
  if (isInKey) return 'passing';
  return 'avoid';
}

export function getTieredChordHighlights(
  chord: ResolvedChord,
  keyRoot: NoteName,
  tuningNotes: NoteName[] = STANDARD_TUNING,
  fretCount = 20,
  tierColors?: Record<NoteTier, string>
): StageHighlight[] {
  const colors = tierColors ?? TIER_COLORS;
  const keyRootSemi = noteToSemitone(keyRoot);
  const chordRootSemi = noteToSemitone(chord.root);
  const tuningSemitones = tuningNotes.map((n) => noteToSemitone(n));
  const highlights: StageHighlight[] = [];

  for (let si = 0; si < tuningSemitones.length; si++) {
    for (let fret = 0; fret <= fretCount; fret++) {
      const semi = (tuningSemitones[si] + fret) % 12;
      const tier = classifyNote(
        semi,
        keyRootSemi,
        chordRootSemi,
        chord.quality
      );
      // "Avoid" notes are intentionally left unhighlighted in guided solo;
      // they remain visible as baseline ghost/non-scale notes.
      if (tier === 'avoid') continue;
      highlights.push({
        fret,
        string: si,
        fillColor: colors[tier],
        borderColor: TIER_BORDER_COLORS[tier],
      });
    }
  }
  return highlights;
}

/* ------------------------------------------------------------------ */
/*  Riff / pattern generator                                           */
/* ------------------------------------------------------------------ */

export interface RiffNote {
  fret: number;
  string: number;
  note: NoteName;
  beat: number;
}

export type RiffStyle = 'pop-anthem' | 'country' | 'blues' | 'rock' | 'chill';

interface StyleConfig {
  contours: number[][];
  rhythms: number[][];
  phraseForms: string[];
  motifRepeat: number;
  chordToneStart: number;
  maxLeap: number;
  breatheEvery: number;
  resolveEnd: number;
}

const RIFF_STYLES: Record<RiffStyle, StyleConfig> = {
  'pop-anthem': {
    contours: [
      [0, 0, 1, 0],
      [0, 1, 0, -1],
      [0, 0, 0, 1],
      [0, 1, 2, 1],
    ],
    rhythms: [
      [1, 0, 1, 1],
      [1, 1, 0, 1],
      [1, 0, 1, 0],
      [1, 1, 1, 0],
    ],
    phraseForms: ['AABA', 'AABB'],
    motifRepeat: 0.85,
    chordToneStart: 0.9,
    maxLeap: 2,
    breatheEvery: 4,
    resolveEnd: 0.8,
  },
  country: {
    contours: [
      [0, 1, 2, 1],
      [0, -1, 0, 1],
      [0, 1, 0, 2],
      [0, 2, 1, 0],
    ],
    rhythms: [
      [1, 1, 1, 1],
      [0, 1, 1, 1],
      [1, 1, 0, 1],
      [1, 0, 1, 1],
    ],
    phraseForms: ['AABB', 'ABAB'],
    motifRepeat: 0.7,
    chordToneStart: 0.85,
    maxLeap: 3,
    breatheEvery: 0,
    resolveEnd: 0.7,
  },
  blues: {
    contours: [
      [0, 1, -1, 0],
      [0, 0, 1, 0],
      [0, -1, 0, 0],
      [0, 1, 0, -1],
    ],
    rhythms: [
      [1, 0, 1, 0],
      [1, 0, 0, 1],
      [1, 1, 0, 0],
      [1, 0, 1, 1],
    ],
    phraseForms: ['AABA', 'ABAB'],
    motifRepeat: 0.6,
    chordToneStart: 0.9,
    maxLeap: 2,
    breatheEvery: 2,
    resolveEnd: 0.9,
  },
  rock: {
    contours: [
      [0, 1, 2, 3],
      [0, 2, 1, 3],
      [0, -1, -2, -1],
      [0, 1, 2, 1],
    ],
    rhythms: [
      [1, 1, 1, 1],
      [1, 1, 0, 1],
      [1, 0, 1, 1],
      [1, 1, 1, 0],
    ],
    phraseForms: ['AABB', 'ABAB', 'AABA'],
    motifRepeat: 0.65,
    chordToneStart: 0.8,
    maxLeap: 4,
    breatheEvery: 0,
    resolveEnd: 0.6,
  },
  chill: {
    contours: [
      [0, 2, 4, 2],
      [0, 1, 0, -1],
      [0, 0, 2, 0],
      [0, 3, 1, 0],
    ],
    rhythms: [
      [1, 0, 0, 1],
      [1, 0, 1, 0],
      [0, 1, 0, 1],
      [1, 0, 0, 0],
    ],
    phraseForms: ['AABA', 'AABB'],
    motifRepeat: 0.8,
    chordToneStart: 0.95,
    maxLeap: 3,
    breatheEvery: 3,
    resolveEnd: 0.85,
  },
};

export const RIFF_STYLE_OPTIONS: { id: RiffStyle; label: string }[] = [
  { id: 'pop-anthem', label: 'Pop Anthem' },
  { id: 'country', label: 'Country / Storyteller' },
  { id: 'blues', label: 'Blues / Soul' },
  { id: 'rock', label: 'Rock / Classic' },
  { id: 'chill', label: 'Chill / Ambient' },
];

function buildScalePositions(
  chord: ResolvedChord,
  keyRoot: NoteName,
  fretCenter: number,
  fretRange: number,
  tuning: NoteName[] = STANDARD_TUNING
): { fret: number; string: number; note: NoteName; semi: number }[] {
  const keyRootSemi = noteToSemitone(keyRoot);
  const chordRootSemi = noteToSemitone(chord.root);
  const tuningSemitones = tuning.map((n) => noteToSemitone(n));
  const positions: {
    fret: number;
    string: number;
    note: NoteName;
    semi: number;
  }[] = [];
  const fretMin = Math.max(0, fretCenter - fretRange);
  const fretMax = Math.min(20, fretCenter + fretRange);

  for (let si = 0; si < tuningSemitones.length; si++) {
    for (let fret = fretMin; fret <= fretMax; fret++) {
      const semi = (tuningSemitones[si] + fret) % 12;
      const tier = classifyNote(
        semi,
        keyRootSemi,
        chordRootSemi,
        chord.quality
      );
      if (tier === 'chord-tone' || tier === 'strong-scale') {
        positions.push({ fret, string: si, note: semitoneToNote(semi), semi });
      }
    }
  }
  // sort low to high by (string desc, fret asc) so index order = ascending pitch
  positions.sort((a, b) => b.string - a.string || a.fret - b.fret);
  return positions;
}

function coinFlip(prob: number): boolean {
  return Math.random() < prob;
}

function pickRandom<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function clampIndex(idx: number, len: number): number {
  return Math.max(0, Math.min(len - 1, idx));
}

function nearestChordToneIndex(
  positions: { note: NoteName; semi: number }[],
  cursor: number,
  chordNotes: Set<number>
): number {
  let best = cursor;
  let bestDist = Infinity;
  for (let i = 0; i < positions.length; i++) {
    if (chordNotes.has(positions[i].semi)) {
      const d = Math.abs(i - cursor);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
  }
  return best;
}

export function generateRiff(
  progression: ResolvedChord[],
  keyRoot: NoteName,
  style: RiffStyle,
  barsPerChord: number
): RiffNote[][] {
  const cfg = RIFF_STYLES[style];
  const phraseForm = pickRandom(cfg.phraseForms);
  const fretCenter = 5 + Math.floor(Math.random() * 8); // 5-12
  const allBeats: RiffNote[][] = [];
  let cursor = 0;

  for (let ci = 0; ci < progression.length; ci++) {
    const chord = progression[ci];
    const positions = buildScalePositions(chord, keyRoot, fretCenter, 4);
    if (positions.length === 0) {
      for (let b = 0; b < barsPerChord * 4; b++) allBeats.push([]);
      continue;
    }
    const chordSemis = new Set(chord.notes.map((n) => noteToSemitone(n) % 12));

    // voice-lead: find nearest position to previous cursor
    if (ci > 0 && allBeats.length > 0) {
      cursor = clampIndex(cursor, positions.length);
    } else {
      cursor = nearestChordToneIndex(
        positions,
        Math.floor(positions.length / 2),
        chordSemis
      );
    }

    const motifCache = new Map<
      string,
      { contour: number[]; rhythm: number[] }
    >();

    for (let bar = 0; bar < barsPerChord; bar++) {
      const formIdx = (ci * barsPerChord + bar) % phraseForm.length;
      const letter = phraseForm[formIdx];

      // breathe bar
      if (cfg.breatheEvery > 0 && bar > 0 && bar % cfg.breatheEvery === 0) {
        const pos = positions[cursor];
        allBeats.push([
          {
            fret: pos.fret,
            string: pos.string,
            note: pos.note,
            beat: allBeats.length,
          },
        ]);
        allBeats.push([]);
        allBeats.push([]);
        allBeats.push([]);
        continue;
      }

      let contour: number[];
      let rhythm: number[];

      if (motifCache.has(letter) && coinFlip(cfg.motifRepeat)) {
        const cached = motifCache.get(letter)!;
        contour = cached.contour;
        rhythm = cached.rhythm;
      } else {
        contour = pickRandom(cfg.contours);
        rhythm = pickRandom(cfg.rhythms);
        motifCache.set(letter, { contour, rhythm });
      }

      for (let beat = 0; beat < 4; beat++) {
        if (!rhythm[beat]) {
          allBeats.push([]);
          continue;
        }
        // on beat 0, anchor to chord tone
        if (beat === 0 && coinFlip(cfg.chordToneStart)) {
          cursor = nearestChordToneIndex(positions, cursor, chordSemis);
        }
        const offset = contour[beat] ?? 0;
        let nextIdx = cursor + offset;
        // clamp leap
        if (Math.abs(nextIdx - cursor) > cfg.maxLeap) {
          nextIdx = cursor + (offset > 0 ? cfg.maxLeap : -cfg.maxLeap);
        }
        cursor = clampIndex(nextIdx, positions.length);

        // resolve end
        if (
          beat === 3 &&
          bar === barsPerChord - 1 &&
          coinFlip(cfg.resolveEnd)
        ) {
          cursor = nearestChordToneIndex(positions, cursor, chordSemis);
        }

        const pos = positions[cursor];
        allBeats.push([
          {
            fret: pos.fret,
            string: pos.string,
            note: pos.note,
            beat: allBeats.length,
          },
        ]);
      }
    }
  }

  return allBeats;
}
